import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaTimes, FaFileImport } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import './Carriers.css';

const API = import.meta.env.VITE_API_URL;
const PAGE_SIZE = 50;
const MAX_IMPORT_ROWS = 500;

const getInitialCarrierLimit = () => {
  try {
    const saved = parseInt(localStorage.getItem('carriersLimit') || `${PAGE_SIZE}`, 10);
    if (Number.isFinite(saved) && saved > 0) return saved;
  } catch {}
  return PAGE_SIZE;
};

const emptyCarrier = {
  name: '',
  contact: '',
  email: '',
  phone: '',
  cellphone: '',
  fax: '',
  website: '',
  accountnumber: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  active: true,
};

const carrierFieldDefinitions = [
  { key: 'name', label: 'Carrier Name', matches: ['name', 'carrier', 'carriername'], required: true },
  { key: 'contact', label: 'Contact', matches: ['contact', 'contactname', 'attn'] },
  { key: 'email', label: 'Email', matches: ['email', 'emailaddress'] },
  { key: 'phone', label: 'Phone', matches: ['phone', 'telephone', 'phone1'] },
  { key: 'cellphone', label: 'Mobile', matches: ['cell', 'mobile', 'phone2'] },
  { key: 'fax', label: 'Fax', matches: ['fax'] },
  { key: 'website', label: 'Website', matches: ['website', 'url'] },
  { key: 'accountnumber', label: 'Account #', matches: ['account', 'acct', 'accountnumber'] },
  { key: 'address', label: 'Address', matches: ['address', 'address1', 'street'] },
  { key: 'city', label: 'City', matches: ['city', 'town'] },
  { key: 'state', label: 'State / Province', matches: ['state', 'province', 'region'] },
  { key: 'zip', label: 'Postal Code', matches: ['zip', 'postal', 'postalcode'] },
  { key: 'country', label: 'Country', matches: ['country'] },
  { key: 'active', label: 'Active', matches: ['active', 'status', 'enabled'], type: 'boolean' },
];

const normalizeMatchKey = (value = '') => (
  value ? value.toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '') : ''
);

const carrierFieldByKey = carrierFieldDefinitions.reduce((acc, field) => {
  acc[field.key] = field;
  return acc;
}, {});

const requiredFieldKeys = carrierFieldDefinitions.filter((f) => f.required).map((f) => f.key);

const interpretBoolean = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const text = String(value).trim().toLowerCase();
  if (!text) return null;
  if (['yes', 'y', 'true', '1', 'active', 'enabled'].includes(text)) return true;
  if (['no', 'n', 'false', '0', 'inactive', 'disabled'].includes(text)) return false;
  return null;
};

const guessInitialMapping = (headers = []) => {
  const normalized = headers.map((header, idx) => ({
    idx,
    match: normalizeMatchKey(header || `col_${idx}`),
  }));
  const used = new Set();
  const mapping = {};
  carrierFieldDefinitions.forEach((field) => {
    const matches = (field.matches || []).map(normalizeMatchKey);
    const entry = normalized.find((col) => matches.includes(col.match) && !used.has(col.idx));
    if (entry) {
      mapping[field.key] = entry.idx;
      used.add(entry.idx);
    }
  });
  return mapping;
};

const buildPayloadFromRow = (row = [], mapping = {}) => {
  const payload = {};
  carrierFieldDefinitions.forEach((field) => {
    const colIdx = mapping[field.key];
    if (colIdx === undefined || colIdx === null) return;
    const raw = row[colIdx];
    if (raw === null || raw === undefined || raw === '') return;
    if (field.type === 'boolean') {
      const boolVal = interpretBoolean(raw);
      if (boolVal !== null) payload[field.key] = boolVal;
      return;
    }
    payload[field.key] = String(raw).trim();
  });
  return payload;
};

function Carriers() {
  const initialLimit = getInitialCarrierLimit();
  const [carriers, setCarriers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: initialLimit, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [limit, setLimit] = useState(initialLimit);
  const [limitInput, setLimitInput] = useState(String(initialLimit));
  const [isTypingLimit, setIsTypingLimit] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(emptyCarrier);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importRows, setImportRows] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importError, setImportError] = useState('');
  const [importStatus, setImportStatus] = useState({ processing: false, processed: 0, total: 0, errors: [] });
  const fileInputRef = useRef(null);

  const resetImportState = () => {
    setImportHeaders([]);
    setImportRows([]);
    setImportMapping({});
    setImportError('');
    setImportStatus({ processing: false, processed: 0, total: 0, errors: [] });
  };

  const fetchCarriers = async (offset = 0, limitOverride = limit) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        limit: String(limitOverride || limit || PAGE_SIZE),
        offset: String(offset),
        sortField: 'name',
        sortOrder: 'ASC',
      });
      if (search.trim()) params.set('q', search.trim());
      if (!showInactive) params.set('active', 'active');
      const res = await fetch(`${API}/api/carriers?${params.toString()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      const json = await res.json();
      setCarriers(Array.isArray(json?.data) ? json.data : []);
      setMeta(json?.meta || { total: 0, limit: limitOverride || limit || PAGE_SIZE, offset });
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to load carriers');
      setCarriers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarriers(0, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showInactive, limit]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Carriers', subtitle: 'Carrier Directory' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, []);

  useEffect(() => {
    if (!isTypingLimit) {
      setLimitInput(String(limit));
    }
  }, [limit, isTypingLimit]);

  const currentPage = useMemo(() => {
    const safeLimit = meta?.limit || limit || PAGE_SIZE;
    return Math.floor((meta?.offset || 0) / safeLimit) + 1;
  }, [meta, limit]);

  const totalPages = useMemo(() => {
    const safeLimit = meta?.limit || limit || PAGE_SIZE;
    if (!safeLimit) return 1;
    return Math.max(1, Math.ceil((meta?.total || 0) / safeLimit));
  }, [meta, limit]);

  const gotoPage = (targetPage) => {
    const safeLimit = meta?.limit || limit || PAGE_SIZE;
    if (!safeLimit) return;
    const last = Math.max(1, Math.ceil((meta?.total || 0) / safeLimit));
    const clamped = Math.min(Math.max(targetPage, 1), last);
    const newOffset = (clamped - 1) * safeLimit;
    fetchCarriers(newOffset, safeLimit);
  };

  const handleLimitUpdate = (raw) => {
    let next = parseInt(raw ?? limitInput, 10);
    if (Number.isNaN(next)) next = limit;
    if (next < 1) next = 1;
    if (next > 10000) next = 10000;
    setLimit(next);
    setLimitInput(String(next));
    setIsTypingLimit(false);
    try { localStorage.setItem('carriersLimit', String(next)); } catch {}
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...emptyCarrier });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (carrier) => {
    setEditing(carrier);
    setFormData({
      name: carrier.name || '',
      contact: carrier.contact || '',
      email: carrier.email || '',
      phone: carrier.phone || '',
      cellphone: carrier.cellphone || '',
      fax: carrier.fax || '',
      website: carrier.website || '',
      accountnumber: carrier.accountnumber || '',
      address: carrier.address || '',
      city: carrier.city || '',
      state: carrier.state || '',
      zip: carrier.zip || '',
      country: carrier.country || '',
      active: carrier.active,
    });
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
    setFormData({ ...emptyCarrier });
    setFormError('');
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveCarrier = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Carrier name is required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = { ...formData };
      const url = editing ? `${API}/api/carriers/${editing.id}` : `${API}/api/carriers`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      await fetchCarriers(meta?.offset || 0, limit);
      closeForm();
    } catch (err) {
      console.error(err);
      setFormError(err.message || 'Failed to save carrier');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedAll = carriers.length > 0 && carriers.every((carrier) => selectedIds.has(carrier.id));
  const hasSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (selectedAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(carriers.map((carrier) => carrier.id)));
    }
  };

  const deleteCarrier = async (carrierId) => {
    if (!window.confirm('Delete this carrier?')) return;
    try {
      const res = await fetch(`${API}/api/carriers/${carrierId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      await fetchCarriers(meta?.offset || 0, limit);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete carrier');
    }
  };

  const restoreCarrier = async (carrierId) => {
    try {
      const res = await fetch(`${API}/api/carriers/${carrierId}/restore`, { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      await fetchCarriers(meta?.offset || 0, limit);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to restore carrier');
    }
  };

  const bulkDeleteSelected = async () => {
    if (!hasSelected || !window.confirm(`Delete ${selectedIds.size} carrier(s)?`)) return;
    setBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        // eslint-disable-next-line no-await-in-loop
        await fetch(`${API}/api/carriers/${id}`, { method: 'DELETE' });
      }
      setSelectedIds(new Set());
      await fetchCarriers(meta?.offset || 0, limit);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete selected carriers');
    } finally {
      setBulkDeleting(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.SheetNames[0];
      if (!firstSheet) throw new Error('No worksheet found.');
      const sheet = workbook.Sheets[firstSheet];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      if (!rows.length) throw new Error('No rows detected in the uploaded file.');
      const headers = rows[0].map((header, idx) => header || `Column ${idx + 1}`);
      const bodyRows = rows.slice(1).filter((row) => Array.isArray(row) && row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== ''));
      if (!bodyRows.length) throw new Error('No carrier rows found under the header.');
      const limitedRows = bodyRows.slice(0, MAX_IMPORT_ROWS);
      setImportHeaders(headers);
      setImportRows(limitedRows);
      setImportMapping(guessInitialMapping(headers));
      setImportError('');
      setImportStatus({ processing: false, processed: 0, total: 0, errors: [] });
    } catch (err) {
      console.error('Carrier import parse error', err);
      setImportError(err.message || 'Failed to read the uploaded file.');
      setImportHeaders([]);
      setImportRows([]);
      setImportMapping({});
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const mappedPreview = useMemo(() => (
    importRows.slice(0, 5).map((row) => buildPayloadFromRow(row, importMapping))
  ), [importRows, importMapping]);

  const readyImportRows = useMemo(() => (
    importRows
      .map((row, idx) => ({ idx, payload: buildPayloadFromRow(row, importMapping) }))
      .filter(({ payload }) => payload && payload.name)
  ), [importRows, importMapping]);

  const requiredMappingsMissing = requiredFieldKeys.some((key) => {
    const field = carrierFieldByKey[key];
    return field?.required && (importMapping[key] === undefined || importMapping[key] === null);
  });

  const startImport = async () => {
    if (!readyImportRows.length) {
      setImportError('No valid rows detected for import.');
      return;
    }
    if (requiredMappingsMissing) {
      setImportError(`Please map required fields: ${requiredFieldKeys.map((key) => carrierFieldByKey[key]?.label || key).join(', ')}`);
      return;
    }
    setImportError('');
    setImportStatus({ processing: true, processed: 0, total: readyImportRows.length, errors: [] });
    const errors = [];
    try {
      for (let i = 0; i < readyImportRows.length; i += 1) {
        const { payload, idx } = readyImportRows[i];
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await fetch(`${API}/api/carriers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.error || `Row ${idx + 2}: Request failed (${res.status})`);
          }
        } catch (err) {
          errors.push({ row: idx + 2, message: err.message || 'Failed to import row' });
        }
        setImportStatus((prev) => ({
          ...prev,
          processed: i + 1,
        }));
      }
      await fetchCarriers(meta?.offset || 0, limit);
    } finally {
      setImportStatus((prev) => ({
        ...prev,
        processing: false,
        errors,
      }));
    }
  };

  return (
    <div className="suppliers-wrapper carriers-wrapper">
      <div className="suppliers-toolbar">
        <div className="search-field">
          <FaSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, contact, city..."
          />
          {search && (
            <button className="icon-btn" onClick={() => setSearch('')} aria-label="Clear search">
              <FaTimes />
            </button>
          )}
        </div>
        <label className="toggle-field">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
        <button className="btn" onClick={() => { setImportOpen((prev) => !prev); if (importOpen) resetImportState(); }}>
          <FaFileImport />
          {importOpen ? 'Hide Import' : 'Import CSV'}
        </button>
        <button
          className="btn danger"
          onClick={bulkDeleteSelected}
          disabled={!hasSelected || bulkDeleting}
          title="Delete selected carriers"
        >
          <FaTrash />
          Delete Selected
        </button>
        <button className="btn primary" onClick={openCreate}>
          <FaPlus />
          New Carrier
        </button>
      </div>

      {importOpen && (
        <div className="suppliers-import-panel">
          <div className="import-panel-header">
            <div>
              <h3>Carrier Import</h3>
              <p>Upload a CSV/Excel file and map columns to carrier fields.</p>
            </div>
            <div className="import-panel-actions">
              <button className="btn" type="button" onClick={resetImportState}>Reset</button>
              <button className="icon-btn" type="button" onClick={() => { setImportOpen(false); resetImportState(); }} aria-label="Close import panel">
                <FaTimes />
              </button>
            </div>
          </div>
          <div className="import-file-row">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={onFileChange}
              accept=".csv,.tsv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            <button className="btn" type="button" onClick={() => fileInputRef.current?.click()}>
              <FaFileImport />
              {importHeaders.length ? 'Replace File' : 'Choose File'}
            </button>
            <span className="import-file-name">
              {importHeaders.length ? `${importRows.length} row(s) ready` : 'No file selected yet'}
            </span>
          </div>
          {importError && <div className="suppliers-error">{importError}</div>}
          {importHeaders.length > 0 && (
            <div className="import-split">
              <div className="import-pane mapping-pane">
                <div className="pane-title">
                  <strong>Carrier Fields</strong>
                  <span>Map each field to a column</span>
                </div>
                <div className="import-fields-list">
                  {carrierFieldDefinitions.map((field) => (
                    <div
                      key={field.key}
                      className={[
                        'import-field-card',
                        field.required ? 'required' : '',
                        importMapping[field.key] != null ? 'assigned' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="field-label">
                        <strong>{field.label}</strong>
                        {field.required && <span className="required-indicator" title="Required">*</span>}
                      </div>
                      <div className="field-assignment">
                        <select
                          value={importMapping[field.key] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value === '' ? null : Number(e.target.value);
                            setImportMapping((prev) => ({ ...prev, [field.key]: val }));
                          }}
                        >
                          <option value="">{importHeaders.length ? 'Select column...' : 'Upload a file first'}</option>
                          {importHeaders.map((header, idx) => (
                            <option key={`header-${idx}`} value={idx}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="import-pane csv-pane">
                <div className="pane-title">
                  <strong>Preview</strong>
                  <span>First {mappedPreview.length || 0} row(s)</span>
                </div>
                {mappedPreview.length ? (
                  <table className="import-preview-table">
                    <thead>
                      <tr>
                        {carrierFieldDefinitions.map((field) => (
                          <th key={field.key}>{field.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mappedPreview.map((row, rowIdx) => (
                        <tr key={`preview-${rowIdx}`}>
                          {carrierFieldDefinitions.map((field) => (
                            <td key={`${field.key}-${rowIdx}`}>
                              {field.type === 'boolean'
                                ? (row[field.key] === undefined ? '—' : (row[field.key] ? 'Yes' : 'No'))
                                : (row[field.key] || '—')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="import-empty-state">Upload a CSV or Excel file to see the preview.</div>
                )}
              </div>
            </div>
          )}
          <div className="import-footer">
            <div className="import-status">
              {importStatus.processing ? (
                <span>Importing {importStatus.processed} / {importStatus.total}...</span>
              ) : readyImportRows.length ? (
                <span>{readyImportRows.length} row{readyImportRows.length === 1 ? '' : 's'} ready</span>
              ) : (
                <span>Waiting for a mapped file...</span>
              )}
              {requiredMappingsMissing && (
                <span className="missing-fields">
                  Required: {requiredFieldKeys.map((key) => carrierFieldByKey[key]?.label || key).join(', ')}
                </span>
              )}
            </div>
            <div className="import-actions">
              <button className="btn" type="button" onClick={resetImportState} disabled={importStatus.processing}>Clear</button>
              <button
                className="btn primary"
                type="button"
                disabled={importStatus.processing || !readyImportRows.length || requiredMappingsMissing}
                onClick={startImport}
              >
                {importStatus.processing ? 'Importing...' : readyImportRows.length ? `Import ${readyImportRows.length} Carrier${readyImportRows.length === 1 ? '' : 's'}` : 'Import Carriers'}
              </button>
            </div>
          </div>
          {importStatus.errors.length > 0 && (
            <div className="suppliers-error">
              <strong>Some rows failed:</strong>
              <ul className="import-error-list">
                {importStatus.errors.slice(0, 5).map((err, idx) => (
                  <li key={`error-${idx}`}>Row {err.row}: {err.message}</li>
                ))}
                {importStatus.errors.length > 5 && <li>Additional errors truncated...</li>}
              </ul>
            </div>
          )}
        </div>
      )}

      {error && <div className="suppliers-error">{error}</div>}

      <div className="suppliers-table-card">
        {loading ? (
          <div className="loading">Loading carriers…</div>
        ) : carriers.length ? (
          <table className="suppliers-table">
            <thead>
              <tr>
                <th className="select-col" data-colkey="select">
                  <input
                    type="checkbox"
                    checked={selectedAll}
                    onChange={toggleSelectAll}
                    aria-label="Select all carriers"
                  />
                </th>
                <th className="status-col" data-colkey="status">Status</th>
                <th className="actions-col" data-colkey="actions">Actions</th>
                <th data-colkey="name">Name</th>
                <th data-colkey="contact">Contact</th>
                <th data-colkey="phone">Phone</th>
                <th data-colkey="location">Location</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((carrier) => (
                <tr key={carrier.id} className={selectedIds.has(carrier.id) ? 'active' : ''}>
                  <td className="select-cell" data-colkey="select">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(carrier.id)}
                      onChange={() => toggleSelect(carrier.id)}
                      aria-label={`Select ${carrier.name || 'carrier'}`}
                    />
                  </td>
                  <td className="status-cell" data-colkey="status">
                    <span className={`status-pill ${carrier.active ? 'active' : 'inactive'}`}>
                      {carrier.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell td-actions" data-colkey="actions">
                    <div className="actions">
                      <button className="mini-btn" title="Edit" onClick={() => openEdit(carrier)}>
                        <FaEdit size={14} />
                      </button>
                      {carrier.active ? (
                        <button className="mini-btn danger" title="Delete" onClick={() => deleteCarrier(carrier.id)}>
                          <FaTrash size={14} />
                        </button>
                      ) : (
                        <button className="mini-btn" title="Restore" onClick={() => restoreCarrier(carrier.id)}>
                          Restore
                        </button>
                      )}
                    </div>
                  </td>
                  <td data-colkey="name">
                    <div className="supplier-name">
                      <span className="supplier-label">{carrier.name || '—'}</span>
                      {carrier.accountnumber && (
                        <span className="supplier-code">{carrier.accountnumber}</span>
                      )}
                    </div>
                    {carrier.website && (
                      <a href={carrier.website.startsWith('http') ? carrier.website : `https://${carrier.website}`} target="_blank" rel="noreferrer">
                        {carrier.website}
                      </a>
                    )}
                  </td>
                  <td data-colkey="contact">
                    <div>{carrier.contact || '—'}</div>
                    {carrier.email && <div className="supplier-muted">{carrier.email}</div>}
                  </td>
                  <td data-colkey="phone">
                    <div>{carrier.phone || '—'}</div>
                    {carrier.cellphone && <div className="supplier-muted">{carrier.cellphone}</div>}
                    {carrier.fax && <div className="supplier-muted">Fax: {carrier.fax}</div>}
                  </td>
                  <td data-colkey="location">
                    <div>{carrier.address || '—'}</div>
                    <div className="supplier-muted">
                      {[carrier.city, carrier.state, carrier.zip].filter(Boolean).join(', ') || '—'}
                    </div>
                    {carrier.country && <div className="supplier-muted">{carrier.country}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">No carriers found.</div>
        )}
      </div>

      <div className="suppliers-pagination carriers-pagination">
        <button className="btn" onClick={() => gotoPage(1)} disabled={currentPage <= 1}>&laquo;</button>
        <button className="btn" onClick={() => gotoPage(currentPage - 1)} disabled={currentPage <= 1}>Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button className="btn" onClick={() => gotoPage(currentPage + 1)} disabled={currentPage >= totalPages}>Next</button>
        <button className="btn" onClick={() => gotoPage(totalPages)} disabled={currentPage >= totalPages}>&raquo;</button>
        <label style={{ marginLeft: 12, fontSize: 13 }}>
          Rows per page:&nbsp;
          <input
            type="number"
            min={1}
            max={10000}
            value={limitInput}
            style={{ width: 60, fontSize: 13, padding: '2px 4px' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleLimitUpdate(e.currentTarget.value);
              } else {
                setIsTypingLimit(true);
              }
            }}
            onChange={(e) => {
              setLimitInput(e.target.value);
            }}
            onBlur={(e) => handleLimitUpdate(e.target.value)}
          />
        </label>
        <span className="suppliers-total">{meta?.total || 0} total</span>
      </div>

      {formOpen && (
        <div className="modal-backdrop suppliers-modal-backdrop" onClick={closeForm}>
          <div className="modal suppliers-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Carrier' : 'New Carrier'}</h3>
              <button className="icon-btn" onClick={closeForm} aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={saveCarrier} className="suppliers-form">
              <div className="modal-body">
                {formError && <div className="suppliers-error">{formError}</div>}
                <div className="form-grid">
                  <label>
                    <span>Carrier Name *</span>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                    />
                  </label>
                  <label>
                    <span>Contact</span>
                    <input name="contact" value={formData.contact} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Email</span>
                    <input name="email" value={formData.email} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Phone</span>
                    <input name="phone" value={formData.phone} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Mobile</span>
                    <input name="cellphone" value={formData.cellphone} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Fax</span>
                    <input name="fax" value={formData.fax} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Website</span>
                    <input name="website" value={formData.website} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Account #</span>
                    <input name="accountnumber" value={formData.accountnumber} onChange={handleFormChange} />
                  </label>
                  <label className="span-2">
                    <span>Address</span>
                    <input name="address" value={formData.address} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>City</span>
                    <input name="city" value={formData.city} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>State / Province</span>
                    <input name="state" value={formData.state} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Postal Code</span>
                    <input name="zip" value={formData.zip} onChange={handleFormChange} />
                  </label>
                  <label>
                    <span>Country</span>
                    <input name="country" value={formData.country} onChange={handleFormChange} />
                  </label>
                  <label className="span-2 checkbox-field">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleFormChange}
                    />
                    Carrier is active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" type="button" onClick={closeForm} disabled={saving}>Cancel</button>
                <button className="btn primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Carrier')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Carriers;
